import React, { useEffect, useState } from "react";
import * as Styled from "./styles.js";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import TextView from "../../../TextView/index.jsx";
import { Box, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ReactComponent as LockIcon } from "@assets/images/segmentEditor/Lock.svg";
import { ReactComponent as UnlockedIcon } from "@assets/images/segmentEditor/lockOpen.svg";

function SettingsSlider({
  sliderValue,
  onValueChange,
  title,
  tooltip,
  min = 0,
  max = 20,
  valueLabel = "px",
  containerStyle = {},
  applyButton = false,
  handleApplyChanges = () => {},
  allowedValues = [],
  step = 1,
  allowDecimal = false,
  fullSlider = false,
  locked = false,
  disabled = false,
  onLockToggle = () => {},
  ...rest
}) {
  const [localValue, setLocalValue] = useState(sliderValue);

  useEffect(() => {
    setLocalValue(sliderValue);
  }, [sliderValue]);

  const snapToAllowedValues = (value) => {
    if (!allowedValues.length) return value;
    let closest = allowedValues[0];
    let minDiff = Math.abs(value - closest);
    for (let i = 1; i < allowedValues.length; i++) {
      const diff = Math.abs(value - allowedValues[i]);
      if (diff < minDiff) {
        minDiff = diff;
        closest = allowedValues[i];
      }
    }

    return closest;
  };

  function snapToStep(value, step) {
    const snapped = Math.round(value / step) * step;

    const stepStr = String(step);
    const decimalPart = stepStr.split(".")[1];
    const decimals = decimalPart ? decimalPart.length : 0;

    // Fix floating artifact (e.g., 1.39999999999 => 1.40):
    return parseFloat(snapped.toFixed(decimals));
  }

  const handleSliderValueChange = (newValue) => {
    if (typeof newValue === "number" && newValue >= min && newValue <= max) {
      const snapped = snapToAllowedValues(newValue);
      onValueChange(snapped);
      setLocalValue(snapped);
    }
  };

  const handleSliderDragging = (newValue) => {
    if (typeof newValue === "number" && newValue >= min && newValue <= max) {
      const snapped = snapToAllowedValues(newValue);
      setLocalValue(snapped);
    }
  };

  const handleValueChange = (e) => {
    const { value } = e.target;
    setLocalValue("");

    if (value.trim() === "") {
      onValueChange("");
      return;
    }

    if (allowDecimal) {
      if (value.endsWith(".")) {
        onValueChange(value);
        setLocalValue(value);
        return;
      }
      const parsedValue = parseFloat(value);
      if (!isNaN(parsedValue) && parsedValue >= min && parsedValue <= max) {
        const snapped = snapToStep(parsedValue, step);

        if (snapped >= min && snapped <= max) {
          onValueChange(snapped);
          setLocalValue(snapped);
        }
      }
      return;
    } else {
      const parsedValue = parseInt(value, 10);
      if (
        parsedValue >= min &&
        parsedValue <= max &&
        Number.isInteger(parsedValue)
      ) {
        onValueChange(parsedValue);
        setLocalValue(parsedValue);
      }
    }
  };

  const handleApplyClick = () => {
    handleApplyChanges(localValue);
  };

  return (
    <>
      <Styled.Container style={containerStyle}>
        <Styled.SliderHeader>
          <Styled.Flexy>
            <TextView variant="subtitle">{title}</TextView>
            {!!tooltip && (
              <Tooltip title={tooltip} placement="top" arrow>
                <InfoOutlinedIcon
                  sx={{
                    color: "#123051",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            )}
          </Styled.Flexy>

          {fullSlider && (
            <Styled.FlexyBaka>
              <Styled.StyledInput2
                value={localValue}
                disableUnderline
                placeholder=""
                onChange={handleValueChange}
                disabled={disabled}
              />
              {valueLabel && <Styled.PxText2>{valueLabel}</Styled.PxText2>}
              {locked ? (
                <LockIcon
                  style={{ height: "24px", width: "24px", cursor: "pointer" }}
                  onClick={onLockToggle}
                />
              ) : (
                <UnlockedIcon
                  style={{ height: "24px", width: "24px", cursor: "pointer" }}
                  onClick={onLockToggle}
                />
              )}
            </Styled.FlexyBaka>
          )}
        </Styled.SliderHeader>

        <Styled.RowDiv>
          <Slider
            min={min}
            max={max}
            value={typeof localValue === "number" ? localValue : 0}
            onChange={handleSliderDragging}
            onChangeComplete={handleSliderValueChange}
            trackStyle={
              disabled
                ? { backgroundColor: "#e9e9e9", height: 8 }
                : { backgroundColor: "#9B5AF8", height: 8 }
            }
            handleStyle={
              disabled
                ? {
                    borderColor: "#e9e9e9",
                    backgroundColor: "#e9e9e9",
                    height: 24,
                    width: 24,
                    marginTop: -8,
                    marginLeft: "10px",
                  }
                : {
                    borderColor: "#9B5AF8",
                    backgroundColor: "#9B5AF8",
                    height: 24,
                    width: 24,
                    marginTop: -8,
                    marginLeft: "10px",
                  }
            }
            railStyle={
              disabled
                ? { backgroundColor: "#e9e9e9", height: 8 }
                : { backgroundColor: "#9B5AF860", height: 8 }
            }
            step={step}
            disabled={disabled}
            style={{ width: fullSlider && "95%" }}
            {...rest}
          />

          {!fullSlider && (
            <>
              <Styled.StyledInput
                value={localValue}
                disableUnderline
                placeholder=""
                onChange={handleValueChange}
              />
              {valueLabel && <Styled.PxText>{valueLabel}</Styled.PxText>}
            </>
          )}
        </Styled.RowDiv>
      </Styled.Container>

      {applyButton && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            marginTop: "8px",
          }}
        >
          <Typography
            sx={{
              cursor: "pointer",
              color: "#9B5AF8",
              transition: "all 0.2s ease-in-out",
              fontSize: "16px",
              fontWeight: "500",
              fontFamily: "DM Sans",

              "&:hover": {
                scale: "1.05",
                transition: "all 0.2s ease-in-out",
              },
            }}
            onClick={handleApplyClick}
          >
            Apply
          </Typography>
        </Box>
      )}
    </>
  );
}

export default SettingsSlider;
